import React, { useState } from "react";
import { message, Modal } from "antd";
import ajaxMethods from "../../../../../api/ajax-methods";
import * as ajaxEndpoints from "../../../../../api/ajax-endpoints";
import { IPendingWalletCreationRequest } from "../../hooks/usePendingWalletCreationRequests";
import moment from "moment";
import { appInsights } from "../../../../../config/appInsights";
import { errorHandler } from "../../../../../helpers/errorHandler";
import useAggregatorEnvironment from "../../../../../custom-hooks/useAggregatorEnvironment";

interface IDeclineWalletModalProps {
  visible: boolean;
  onClose: () => void;
  walletData: IPendingWalletCreationRequest | null;
  onSuccess: () => void;
}

const DeclineWalletModal: React.FC<IDeclineWalletModalProps> = ({
  visible,
  onClose,
  walletData,
  onSuccess,
}) => {
  const [comment, setComment] = useState("");
  const [pin, setPin] = useState("");
  const [loadingState, setLoadingState] = useState<boolean>(false);

  const { data: onboardingData, status: onboardingStatus } =
    useAggregatorEnvironment({
      id: walletData?.targetId,
      sourceAppId: 3,
    });

  const handleDecline = async () => {
    if (!walletData) return;

    setLoadingState(true);

    try {
      const response = await ajaxMethods.post(
        ajaxEndpoints.DECLINE_WALLET_CREATION_REQUEST,
        {
          reference: walletData.makerCheckerReference,
          comment,
          pin,
        },
      );

      message.success(
        response?.data?.message || "Wallet creation declined successfully",
      );
      handleClose();
      onSuccess();
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "DeclineWalletModal.tsx",
        },
      });

      message.error(errorHandler(error));
    } finally {
      setLoadingState(false);
    }
  };

  const handleClose = () => {
    setComment("");
    setPin("");
    setLoadingState(false);
    onClose();
  };

  if (!walletData) return null;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      closable
      maskClosable
    >
      <div className="Modal__body">
        <h3
          className="Modal__title p-b-1"
          style={{ color: "#dc3545", fontWeight: 700, fontSize: "1.5rem" }}
        >
          Decline Wallet Creation Request
        </h3>
        <div className="m-b-30">
          <h5>
            Are you sure you want to decline the following wallet creation
            request?
          </h5>

          <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
            <div className="row mb-3">
              <div className="col-md-6">
                <p>
                  <strong>Maker Checker Reference:</strong>
                  <br />
                  {walletData.makerCheckerReference}
                </p>
              </div>
              <div className="col-md-6">
                <p>
                  <strong>Target ID:</strong>
                  <br />
                  {walletData.targetId}
                </p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <p>
                  <strong>Consumer Type:</strong>
                  <br />
                  {walletData.consumerType}
                </p>
              </div>
              <div className="col-md-6">
                <p>
                  <strong>Client Type:</strong>
                  <br />
                  {walletData.clientType}
                </p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <p>
                  <strong>Currency:</strong>
                  <br />
                  {walletData.currency}
                </p>
              </div>
              <div className="col-md-6">
                <p>
                  <strong>Provider Type:</strong>
                  <br />
                  {walletData.providerType}
                </p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <p>
                  <strong>Effective Date:</strong>
                  <br />
                  {moment(walletData.effectiveDate).format(
                    "DD-MM-YYYY HH:mm:ss",
                  )}
                </p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3">
                <p>
                  <strong>Digital Bank:</strong>
                  <br />
                  <span
                    className={`badge ${
                      walletData.isDigitalBank
                        ? "badge-success"
                        : "badge-secondary"
                    }`}
                  >
                    {walletData.isDigitalBank ? "Yes" : "No"}
                  </span>
                </p>
              </div>
              <div className="col-md-3">
                <p>
                  <strong>External Wallet:</strong>
                  <br />
                  <span
                    className={`badge ${
                      walletData.isExternalWallet
                        ? "badge-warning"
                        : "badge-secondary"
                    }`}
                  >
                    {walletData.isExternalWallet ? "Yes" : "No"}
                  </span>
                </p>
              </div>
              <div className="col-md-3">
                <p>
                  <strong>Created By Admin:</strong>
                  <br />
                  <span
                    className={`badge ${
                      walletData.createdByAdmin
                        ? "badge-info"
                        : "badge-secondary"
                    }`}
                  >
                    {walletData.createdByAdmin ? "Yes" : "No"}
                  </span>
                </p>
              </div>
              <div className="col-md-3">
                <p>
                  <strong>Post No Debit:</strong>
                  <br />
                  <span
                    className={`badge ${
                      walletData.postNoDebit
                        ? "badge-warning"
                        : "badge-secondary"
                    }`}
                  >
                    {walletData.postNoDebit ? "Yes" : "No"}
                  </span>
                </p>
              </div>
            </div>

            <div className="row mb-3">
              {walletData?.clientType === "Organisation" && (
                <div className="col-md-12">
                  <p>
                    <strong>Onboarding Status:</strong>
                    <br />
                    {onboardingStatus === "loading" ? (
                      <span className="badge badge-secondary">
                        <i className="fa fa-spinner fa-spin" /> Loading...
                      </span>
                    ) : (
                      <span
                        className={`badge ${
                          onboardingData?.statusName === "Customer onboarded"
                            ? "badge-success"
                            : "badge-danger"
                        }`}
                      >
                        {onboardingData?.statusName || "N/A"}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <p>
                  <strong>Note:</strong>
                  <br />
                  <span
                    style={{
                      display: "block",
                      padding: "0.5rem",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      marginTop: "0.25rem",
                    }}
                  >
                    {walletData.note || "No note provided"}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <form>
            <label style={{ width: "100%", marginBottom: "1rem" }}>
              <strong>Comment (Required)</strong>
              <textarea
                className="form-control"
                name="comment"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Reason for declining this wallet creation request"
              />
            </label>
            <label style={{ width: "50%" }}>
              <strong>Generated 2FA code</strong>
              <input
                className="form-control"
                name="pin"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter 2FA code"
              />
            </label>
          </form>

          <div className="Form__action-buttons m-t-2">
            <button
              type="submit"
              onClick={handleDecline}
              disabled={!comment || !pin || loadingState}
              className="btn zoom-element btn btn-danger login-btn m-0 mr-auto"
            >
              {loadingState ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Declining...
                </>
              ) : (
                <>
                  <i className="fa fa-ban" /> Decline
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="btn zoom-element btn login-btn m-0 ml-auto"
            >
              <i className="fa fa-times-circle" /> Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeclineWalletModal;
